import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Button,
  Snackbar,
  Alert,
  Chip,
  ListItemIcon,
  Paper,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Popover,
  Avatar,
} from "@mui/material";
import {
  Description as FileIcon,
  PictureAsPdf as PdfIcon,
  Article as DocIcon,
  Image as ImageIcon,
  TableChart as ExcelIcon,
  Download as DownloadIcon,
  CloudDownload as BulkDownloadIcon,
  Refresh as RefreshIcon,
  Visibility as PreviewIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  ChatBubble as CommentIcon,
} from "@mui/icons-material";
import axios from "axios";
import { useSelector } from "react-redux";
import { useRoleLayout } from "../hooks/useRoleLayout";
import config from "../config.js";

const ClientFiles = ({ refreshTrigger }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(null);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const [comments, setComments] = useState([]);
  const [isCommenting, setIsCommenting] = useState(false);
  const [newCommentCoords, setNewCommentCoords] = useState({ x: 0, y: 0 });
  const [newCommentText, setNewCommentText] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentAnchor, setCommentAnchor] = useState(null);
  const [selectedComment, setSelectedComment] = useState(null);
  const containerRef = useRef(null);

  const { API_BASE_URL } = config;
  const { Layout } = useRoleLayout();
  const kycSelectedCompanyId = useSelector(
    (state) => state.kyc?.selectedCompanyId
  );
  const activeClient = useSelector((state) => state.client?.activeClient);
  const selectedClient = useSelector((state) => state.client?.selectedClient);
  const currentUser = useSelector((state) => state.auth?.user);

  const clientToUse = activeClient || selectedClient;

  const fetchFiles = useCallback(async () => {
    if (!kycSelectedCompanyId) {
      setError("No company selected");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${API_BASE_URL}/api/v1/getClientFilesDetails/${kycSelectedCompanyId}`
      );

      if (response.data.success) {
        const filesData = response.data.data?.files || response.data.files || [];
        const sortedFiles = filesData.sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate));
        setFiles(sortedFiles);
      } else {
        setFiles([]);
      }
    } catch (err) {
      console.error("Error fetching files:", err);
      setError("Failed to load files");
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, kycSelectedCompanyId]);

  const fetchComments = useCallback(async (fileId) => {
    try {
      setLoadingComments(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/files/${fileId}/comments`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setComments(response.data.data || []);
    } catch (err) {
      console.error("Error fetching comments:", err);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    if (kycSelectedCompanyId) {
      fetchFiles();
    }
  }, [kycSelectedCompanyId, refreshTrigger, fetchFiles]);

  useEffect(() => {
    if (previewOpen && previewFile) {
      fetchComments(previewFile._id);
      setIsCommenting(false);
      setNewCommentText("");
    }
  }, [previewOpen, previewFile, fetchComments]);

  const downloadFile = async (fileUrl, fileName) => {
    setDownloadingFile(fileName);
    try {
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = fileName;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSnackbar({
        open: true,
        message: "File download started",
        severity: "success",
      });
    } catch (err) {
      console.error("Error downloading file:", err);
      setSnackbar({
        open: true,
        message: "Failed to download file",
        severity: "error",
      });
    } finally {
      setDownloadingFile(null);
    }
  };

  const downloadAllFiles = async () => {
    if (!kycSelectedCompanyId) return;
    
    setBulkDownloading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/downloadAllFiles/${kycSelectedCompanyId}`,
        {
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `client_files_${kycSelectedCompanyId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: "All files downloaded successfully",
        severity: "success",
      });
    } catch (err) {
      console.error("Error downloading all files:", err);
      setSnackbar({
        open: true,
        message: "Failed to download all files",
        severity: "error",
      });
    } finally {
      setBulkDownloading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleContainerClick = (e) => {
    if (isCommenting || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    if (x >= 0 && x <= 100 && y >= 0 && y <= 100) {
      setNewCommentCoords({ x, y });
      setIsCommenting(true);
      setNewCommentText("");
    }
  };

  const handleSubmitComment = async () => {
    if (!newCommentText.trim() || !previewFile) return;

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/files/${previewFile._id}/comments`,
        {
          commentText: newCommentText,
          x_coordinate: newCommentCoords.x,
          y_coordinate: newCommentCoords.y,
          kycId: kycSelectedCompanyId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setComments([...comments, response.data.data]);
      setIsCommenting(false);
      setNewCommentText("");

      setSnackbar({
        open: true,
        message: "Comment added successfully",
        severity: "success",
      });
    } catch (err) {
      console.error("Error submitting comment:", err);
      setSnackbar({
        open: true,
        message: "Failed to add comment",
        severity: "error",
      });
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API_BASE_URL}/api/v1/comments/${commentId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setComments(comments.filter(c => c._id !== commentId));
      setCommentAnchor(null);

      setSnackbar({
        open: true,
        message: "Comment deleted successfully",
        severity: "success",
      });
    } catch (err) {
      console.error("Error deleting comment:", err);
      setSnackbar({
        open: true,
        message: "Failed to delete comment",
        severity: "error",
      });
    }
  };

  const handleCommentClick = (e, comment) => {
    e.stopPropagation();
    setSelectedComment(comment);
    setCommentAnchor(e.currentTarget);
  };

  const handleCloseCommentPopover = () => {
    setCommentAnchor(null);
    setSelectedComment(null);
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return <FileIcon fontSize="small" />;
    const ext = fileName.split(".").pop()?.toLowerCase();

    if (["pdf"].includes(ext))
      return <PdfIcon fontSize="small" sx={{ color: "#F44336" }} />;
    if (["doc", "docx"].includes(ext))
      return <DocIcon fontSize="small" sx={{ color: "#2196F3" }} />;
    if (["xls", "xlsx"].includes(ext))
      return <ExcelIcon fontSize="small" sx={{ color: "#4CAF50" }} />;
    if (["jpg", "jpeg", "png", "gif"].includes(ext))
      return <ImageIcon fontSize="small" sx={{ color: "#FF9800" }} />;
    return <FileIcon fontSize="small" sx={{ color: "#9E9E9E" }} />;
  };

  const formatFileSize = (sizeStr) => {
    if (!sizeStr) return "0 B";
    return sizeStr;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getTotalFileSize = () => {
    return files.reduce((total, file) => {
      const sizeStr = file.fileSize || "0 B";
      const match = sizeStr.match(/(\d+\.?\d*)\s*(\w+)/);
      if (match) {
        const value = parseFloat(match[1]);
        const unit = match[2].toUpperCase();
        const multipliers = { B: 1, KB: 1024, MB: 1024 * 1024, GB: 1024 * 1024 * 1024 };
        return total + (value * (multipliers[unit] || 1));
      }
      return total;
    }, 0);
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handlePreview = (file) => {
    setPreviewFile(file);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewFile(null);
    setComments([]);
    setIsCommenting(false);
    setNewCommentText("");
  };

  const canPreview = (fileName) => {
    if (!fileName) return false;
    const ext = fileName.split(".").pop()?.toLowerCase();
    return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext);
  };

  const getFileExtension = (fileName) => {
    if (!fileName) return '';
    return fileName.split(".").pop()?.toLowerCase();
  };

  const renderPreviewContent = () => {
    if (!previewFile) return null;

    const ext = getFileExtension(previewFile.fileName);
    const imageFormats = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];

    const previewStyles = {
      position: 'relative',
      width: '100%',
      height: '70vh',
      overflow: 'auto',
      cursor: 'crosshair',
    };

    if (ext === 'pdf') {
      return (
        <Box sx={previewStyles} ref={containerRef} onClick={handleContainerClick} onMouseMove={(e) => e.currentTarget}>
          <iframe
            src={previewFile.fileUrl}
            width="100%"
            height="100%"
            style={{ border: 'none', pointerEvents: 'auto', display: 'block' }}
            title={previewFile.fileName}
          />
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
            {renderCommentPins()}
            {renderCommentInput()}
          </Box>
        </Box>
      );
    } else if (imageFormats.includes(ext)) {
      return (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          maxHeight: '70vh',
          overflow: 'auto',
          bgcolor: '#f5f5f5',
          p: 2,
        }} ref={containerRef} onClick={handleContainerClick}>
          <img
            src={previewFile.fileUrl}
            alt={previewFile.fileName}
            style={{
              maxWidth: '100%',
              maxHeight: '70vh',
              objectFit: 'contain',
            }}
          />
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
            {renderCommentPins()}
            {renderCommentInput()}
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Preview not available for this file type
        </Typography>
      </Box>
    );
  };

  const renderCommentPins = () => {
    return comments.map((comment) => (
      <Box
        key={comment._id}
        sx={{
          position: 'absolute',
          left: `${comment.x_coordinate}%`,
          top: `${comment.y_coordinate}%`,
          transform: 'translate(-50%, -50%)',
          cursor: 'pointer',
          zIndex: 10,
        }}
        onClick={(e) => handleCommentClick(e, comment)}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: '#2196F3',
            fontSize: '0.75rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s',
          }}
        >
          <CommentIcon sx={{ fontSize: '0.9rem' }} />
        </Avatar>
      </Box>
    ));
  };

  const renderCommentInput = () => {
    if (!isCommenting) return null;

    return (
      <Box
        sx={{
          position: 'absolute',
          left: `${newCommentCoords.x}%`,
          top: `${newCommentCoords.y}%`,
          transform: 'translate(-50%, -50%)',
          zIndex: 20,
        }}
      >
        <Paper
          sx={{
            p: 1.5,
            minWidth: 250,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Add a comment..."
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
            size="small"
            sx={{ mb: 1 }}
            autoFocus
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              onClick={handleSubmitComment}
              disabled={!newCommentText.trim()}
            >
              Post
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setIsCommenting(false);
                setNewCommentText("");
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  };

  if (!clientToUse || !kycSelectedCompanyId) {
    return (
      <Layout>
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Please select a client and company to view files
          </Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Client Files
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Client: {clientToUse?.username || clientToUse?.name}
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchFiles}
              disabled={loading}
            >
              Refresh
            </Button>
            {files.length > 0 && (
              <Button
                size="small"
                variant="contained"
                startIcon={<BulkDownloadIcon />}
                onClick={downloadAllFiles}
                disabled={bulkDownloading}
              >
                {bulkDownloading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  `Download All (${files.length})`
                )}
              </Button>
            )}
          </Stack>
        </Box>

        {files.length > 0 && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Total Files: {files.length} • Total Size: {formatBytes(getTotalFileSize())}
            </Typography>
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ mt: 2 }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}

          {!loading && !error && files.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic", py: 2, textAlign: 'center' }}
            >
              No files found
            </Typography>
          )}

          {!loading && !error && files.length > 0 && (
            <List dense sx={{ maxHeight: 400, overflowY: "auto" }}>
              {files.map((file) => (
                <ListItem
                  key={file._id}
                  sx={{
                    borderRadius: 1,
                    "&:hover": { bgcolor: "#f5f5f5" },
                    mb: 0.5,
                    border: "1px solid #e0e0e0",
                    p: 1.5,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {getFileIcon(file.fileName)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, fontSize: "0.85rem" }}
                        >
                          {file.fileName}
                        </Typography>
                        {file.category && (
                          <Chip
                            label={file.category}
                            size="small"
                            sx={{ height: 20, fontSize: "0.7rem" }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                        {formatFileSize(file.fileSize)} • {formatDate(file.uploadDate)}
                        {file.subCategory && ` • ${file.subCategory}`}
                      </Typography>
                    }
                  />
                  <Stack direction="row" spacing={1}>
                    {canPreview(file.fileName) && (
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<PreviewIcon sx={{ fontSize: 14 }} />}
                        sx={{
                          minWidth: "auto",
                          p: "4px 12px",
                          fontSize: "0.7rem",
                        }}
                        onClick={() => handlePreview(file)}
                      >
                        Preview
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                      sx={{
                        minWidth: "auto",
                        p: "4px 12px",
                        fontSize: "0.7rem",
                      }}
                      onClick={() => downloadFile(file.fileUrl, file.fileName)}
                      disabled={downloadingFile === file.fileName}
                    >
                      {downloadingFile === file.fileName ? (
                        <CircularProgress size={14} />
                      ) : (
                        "Download"
                      )}
                    </Button>
                  </Stack>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Paper>

      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Box>
            <Typography variant="h6" component="span">
              {previewFile?.fileName}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary">
              {formatFileSize(previewFile?.fileSize)} • {formatDate(previewFile?.uploadDate)} • {comments.length} comments
            </Typography>
          </Box>
          <IconButton onClick={handleClosePreview} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {loadingComments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
              <CircularProgress />
            </Box>
          ) : (
            renderPreviewContent()
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => {
              if (previewFile) {
                downloadFile(previewFile.fileUrl, previewFile.fileName);
              }
            }}
            variant="contained"
          >
            Download
          </Button>
          <Button onClick={handleClosePreview}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={Boolean(commentAnchor)}
        anchorEl={commentAnchor}
        onClose={handleCloseCommentPopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Paper sx={{ p: 2, minWidth: 300 }}>
          {selectedComment && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {selectedComment.username}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(selectedComment.createdAt)}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {selectedComment.commentText}
              </Typography>
              {currentUser?._id === selectedComment.userId && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDeleteComment(selectedComment._id)}
                  fullWidth
                >
                  Delete
                </Button>
              )}
            </Box>
          )}
        </Paper>
      </Popover>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientFiles;